<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import EmptyState from '@/components/base/EmptyState.vue';
import type { MenuItem } from '@/types/menu';
import MenuItemActionsMenu from './MenuItemActionsMenu.vue';
import MenuItemPriceDisplay from './MenuItemPriceDisplay.vue';
import MenuItemStatusBadge from './MenuItemStatusBadge.vue';

interface Props {
  menuItems: MenuItem[];
  searchQuery: string;
  canManageItems: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  editItem: [item: MenuItem];
  deleteItem: [item: MenuItem];
  toggleAvailability: [item: MenuItem];
}>();

const { t } = useI18n();

function getImageUrl(item: MenuItem): string | undefined {
  return item.imageUrl || item.image_url;
}

function getItemCategoryName(item: MenuItem): string | null {
  if (!item.category) {
    return null;
  }

  return typeof item.category === 'string' ? item.category : item.category.name;
}
</script>

<template>
  <div class="block md:hidden px-4">
    <EmptyState
      v-if="menuItems.length === 0"
      icon="search"
      :title="searchQuery.trim() ? (t('app.common.no_results') || 'No se encontraron productos') : t('app.views.menu.list.empty')"
      variant="minimal"
      size="sm"
    />

    <div
      v-else
      class="space-y-3"
    >
      <div
        v-for="item in menuItems"
        :key="item.id"
        :data-dropdown-container="`menu-item-${item.id}`"
        class="bg-white dark:bg-gray-900 rounded-lg shadow border border-gray-200 dark:border-gray-800 p-3 relative hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div
          class="absolute top-2 right-2 z-30"
          @click.stop
        >
          <MenuItemActionsMenu
            :item="item"
            :dropdown-id="`menu-item-${item.id}`"
            :can-manage="canManageItems"
            @edit="emit('editItem', $event)"
            @toggle-availability="emit('toggleAvailability', $event)"
            @delete="emit('deleteItem', $event)"
          />
        </div>

        <div class="flex items-start gap-2 mb-2">
          <div
            v-if="getImageUrl(item)"
            class="flex-shrink-0"
          >
            <img
              class="h-14 w-14 rounded-lg object-cover"
              :src="getImageUrl(item)"
              :alt="item.name"
            >
          </div>

          <div class="flex-1 min-w-0 pr-7">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{ item.name }}
            </h3>
            <div class="mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span
                v-if="getItemCategoryName(item)"
                class="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-950 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-100"
              >
                {{ getItemCategoryName(item) }}
              </span>
              <MenuItemStatusBadge
                :item="item"
                variant="mobile"
                class="py-0.5"
              />
            </div>
          </div>
        </div>

        <MenuItemPriceDisplay
          :item="item"
          variant="mobile"
        />
      </div>
    </div>
  </div>
</template>
